import { useEffect, useState } from 'react';
import { FieldErrors } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { buildAgentName } from 'hooks/useGruAgent/helpers';
import { getWorkflow, listWorkflows, WorkflowListItem } from 'services/gru/gruApi';
import { useCurrentPartner } from 'services/repositories/partners/partners';
import { useCurrentUser } from 'services/repositories/user/user';
import { FEATURE_FLAGS } from 'support/helpers/featureFlags/featureFlags';
import { isProduction } from 'support/helpers/generic/generic';
import { IssueResolverType, MessageV2DTO, PartnerDTO, UserDTO } from 'support/types';

import { hasLineItemIdentifierErrors } from '../helpers';
import { WebEDIAgentsTabAvailability } from '../types';

type UseAgentsTabAvailabilityParams = {
  message: MessageV2DTO | undefined;
  issueResolver: IssueResolverType;
  formErrors: FieldErrors;
  isValidationComplete: boolean;
};

export const AGENT_WORKFLOWS_QUERY_KEY = 'gru-agent-workflows';

const SUCCESSFUL_WORKFLOW_STATUSES = new Set(['running', 'awaiting_approval', 'applied']);

type UseAgentsTabAvailabilityReturn = {
  agentsTabAvailability: WebEDIAgentsTabAvailability;
  isLoading: boolean;
  showAgentsTab: boolean;
  hasIdentifierErrors: boolean;
  hasEnrichmentApplied: boolean;
  isValidationComplete: boolean;
  agentName: string;
  currentWorkflow: Awaited<ReturnType<typeof getWorkflow>> | null;
};

export const useAgentsTabAvailability = ({
  message,
  issueResolver,
  formErrors,
  isValidationComplete,
}: UseAgentsTabAvailabilityParams): UseAgentsTabAvailabilityReturn => {
  const isFlagEnabled = Boolean(useFeatureFlagEnabled(FEATURE_FLAGS.PORTAL_CROSS_DOCUMENT_ENRICHMENT_AGENT));
  // Hard-disable the AI agent in production/staging regardless of PostHog flag configuration.
  // This prevents accidental flag misconfiguration from exposing the feature to all users.
  const isFeatureEnabled = !isProduction() && isFlagEnabled;
  const { data: currentUser } = useCurrentUser();
  const { data: currentPartner } = useCurrentPartner();
  const [currentWorkflow, setCurrentWorkflow] = useState<Awaited<ReturnType<typeof getWorkflow>> | null>(null);
  const isIssueResolver = issueResolver === IssueResolverType.CURRENT_PARTNER;

  const shouldCheckAgentStatus = isFeatureEnabled && !!message && !!currentPartner && currentUser !== undefined;

  const agentName = getAgentName(message, currentPartner, currentUser);

  const {
    data: workflows,
    isLoading: isLoadingWorkflowData,
    isError: isWorkflowError,
  } = useQuery({
    queryKey: [AGENT_WORKFLOWS_QUERY_KEY, agentName],
    queryFn: () => listWorkflows(agentName),
    enabled: shouldCheckAgentStatus && !!agentName,
  });

  // `showAgentsTab` depends on `hasEnrichmentApplied` (derived from workflows) for non-blocked
  // messages. While the workflow query is in flight, showAgentsTab may be a false negative —
  // gate loading at this layer so the util's `!showAgentsTab → DISABLED` short-circuit only
  // fires against post-fetch data. On error, treat as settled (not pending) so the sidebar
  // doesn't get stuck in an infinite loading state.
  const isWorkflowDataPending =
    shouldCheckAgentStatus && !isWorkflowError && (isLoadingWorkflowData || workflows === undefined);

  const hasIdentifierErrors = isValidationComplete && hasLineItemIdentifierErrors(formErrors);
  // Keep both 'enrichment' and 'cross-document-enrichment' until gru only returns the latter.
  const hasEnrichmentApplied =
    workflows?.some((w) => ['enrichment', 'cross-document-enrichment'].includes(w.type) && w.applied_at !== null) ??
    false;

  useEffect(() => {
    let cancelled = false;
    const fetchCurrentWorkflow = async () => {
      if (workflows && workflows.length > 0) {
        const latestWorkflow = workflows[workflows.length - 1];
        try {
          const workflowDetails = await getWorkflow(agentName, latestWorkflow.id);
          if (!cancelled) {
            setCurrentWorkflow(workflowDetails);
          }
        } catch {
          if (!cancelled) {
            setCurrentWorkflow(null);
          }
        }
      } else {
        if (!cancelled) {
          setCurrentWorkflow(null);
        }
      }
    };

    fetchCurrentWorkflow();
    return () => {
      cancelled = true;
    };
  }, [workflows, agentName]);

  const hasAnyErrors = !!message?.blocking_error;
  const isIssueResolverWithErrors = isIssueResolver && hasAnyErrors;
  const canShowAgentsTab = isFeatureEnabled && (isIssueResolverWithErrors || hasEnrichmentApplied);

  const agentsTabAvailability = isWorkflowDataPending
    ? WebEDIAgentsTabAvailability.LOADING
    : deriveAgentsTabAvailability({
        isLoadingWorkflowData,
        canShowAgentsTab,
        hasIdentifierErrors,
        isValidationComplete,
        workflows,
      });

  return {
    agentsTabAvailability,
    isLoading: agentsTabAvailability === WebEDIAgentsTabAvailability.LOADING,
    showAgentsTab:
      agentsTabAvailability === WebEDIAgentsTabAvailability.DEFAULT ||
      agentsTabAvailability === WebEDIAgentsTabAvailability.ENABLED,
    hasIdentifierErrors,
    hasEnrichmentApplied,
    isValidationComplete,
    agentName,
    currentWorkflow,
  };
};

/**
 * Resolves the agent tab's availability on initial page load. Drives:
 *   DISABLED → no tab shown
 *   LOADING  → sidebar renders spinner until availability resolves
 *   ENABLED  → tab shown, Overview is default
 *   DEFAULT  → tab shown, AI Agent is default
 */
const deriveAgentsTabAvailability = ({
  isLoadingWorkflowData,
  canShowAgentsTab,
  hasIdentifierErrors,
  isValidationComplete,
  workflows,
}: {
  isLoadingWorkflowData: boolean;
  canShowAgentsTab: boolean;
  hasIdentifierErrors: boolean;
  isValidationComplete: boolean;
  workflows: Array<WorkflowListItem> | undefined;
}): WebEDIAgentsTabAvailability => {
  if (!canShowAgentsTab) {
    return WebEDIAgentsTabAvailability.DISABLED;
  }

  const isStillFetchingWorkflows = isLoadingWorkflowData || workflows === undefined;
  if (isStillFetchingWorkflows) {
    return WebEDIAgentsTabAvailability.LOADING;
  }

  // `applied_at` is checked independently of status: an older workflow may keep `applied_at` set
  // after transitioning past `applied` (e.g. `applied → completed`), and we still want the tab
  // defaulted so the prior enrichment remains visible.
  const hasWorkflowInSuccessfulState = workflows.some(
    (w) => w.applied_at !== null || SUCCESSFUL_WORKFLOW_STATUSES.has(w.status),
  );
  if (hasWorkflowInSuccessfulState) {
    return WebEDIAgentsTabAvailability.DEFAULT;
  }

  if (!isValidationComplete) {
    return WebEDIAgentsTabAvailability.LOADING;
  }

  const willAgentAutoTrigger = hasIdentifierErrors && workflows.length === 0;
  if (willAgentAutoTrigger) {
    return WebEDIAgentsTabAvailability.DEFAULT;
  }

  return WebEDIAgentsTabAvailability.ENABLED;
};

const getAgentName = (
  message: MessageV2DTO | undefined,
  currentPartner: PartnerDTO | undefined,
  currentUser: UserDTO | undefined,
): string =>
  message?.id && currentPartner?.id ? buildAgentName(message.id, currentPartner.id, currentUser?.isAdmin ?? false) : '';
